import { MAX_RECORDS_PER_PERMISSION_READ } from '@omnea-digital/authz-model'
import DataLoader from 'dataloader'
import chunk from 'lodash/chunk'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'
import { RecordPermissionsResponseError } from './errors'
import type { AskedRecordPermission } from './askedRecord'
import type { RecordPermissionsEntity } from '@omnea-digital/authz-model'

type PermissionQuestion = { entity: RecordPermissionsEntity; permission: string }

const fetchPermissionOnRecords = async (
  token: string,
  { entity, permission }: PermissionQuestion,
  recordIds: readonly string[]
): Promise<ReadonlyMap<string, boolean>> => {
  const permissionsClient = new PermissionsHttpClient({ authorization: token })
  const { data: response, error } = await permissionsClient.POST('/entities/{entity}/permissions/{permission}/check', {
    params: { path: { entity, permission } },
    body: { recordIds: [...recordIds] },
  })
  throwIfApiError(error)
  if (response == null) throw new RecordPermissionsResponseError({ reason: 'noBatchBody', entity, recordIds })
  return new Map(response.records.map(({ recordId, allowed }) => [recordId, allowed]))
}

const notAnswered = (entity: RecordPermissionsEntity, recordId: string) =>
  new RecordPermissionsResponseError({ reason: 'notAnswered', entity, recordId })

// DataLoader rejects one load by being handed an Error in its place, so a failed chunk fails only its own records.
const fetchChunkAnswers = async (
  token: string,
  question: PermissionQuestion,
  recordIds: readonly string[]
): Promise<(readonly [string, boolean | Error])[]> => {
  try {
    const allowedByRecordId = await fetchPermissionOnRecords(token, question, recordIds)
    return recordIds.map((recordId) => [recordId, allowedByRecordId.get(recordId) ?? notAnswered(question.entity, recordId)])
  } catch (error) {
    return recordIds.map((recordId) => [recordId, error instanceof Error ? error : notAnswered(question.entity, recordId)])
  }
}

type RecordPermissionBatcher = {
  loader: DataLoader<string, boolean>
  getToken: () => Promise<string>
}

const batchersByQuestion = new Map<string, RecordPermissionBatcher>()

const createBatcher = (question: PermissionQuestion, getToken: () => Promise<string>): RecordPermissionBatcher => {
  const batcher: RecordPermissionBatcher = {
    getToken,
    loader: new DataLoader<string, boolean>(
      async (recordIds) => {
        const token = await batcher.getToken()
        // React Query can call load twice for one id when it refetches, so duplicates go before the split: each id
        // is sent once and every load of it gets the same answer. The split is the endpoint's own cap: it answers a
        // longer list with a 400, failing every record in it.
        const recordIdChunks = chunk([...new Set(recordIds)], MAX_RECORDS_PER_PERMISSION_READ)
        const chunkAnswers = await Promise.all(
          recordIdChunks.map((recordIdChunk) => fetchChunkAnswers(token, question, recordIdChunk))
        )
        const answerByRecordId = new Map(chunkAnswers.flat())
        return recordIds.map((recordId) => answerByRecordId.get(recordId) ?? notAnswered(question.entity, recordId))
      },
      {
        // React Query holds the answers and invalidateRecordAuthorization clears them there; a second cache here
        // would keep serving a revoked permission after that invalidation.
        cache: false,
        // One macrotask: every row a render mounts asks before this runs, so a list shares one call.
        batchScheduleFn: (dispatch) => setTimeout(dispatch, 0),
      }
    ),
  }
  return batcher
}

const getOrCreateBatcher = (getToken: () => Promise<string>, userId: string, question: PermissionQuestion) => {
  // One call answers one permission of one entity, and answers depend on who is asking.
  const key = `${userId}:${question.entity}:${question.permission}`
  const batcher = batchersByQuestion.get(key) ?? createBatcher(question, getToken)
  // A batch sends one token for every question in it. They all come from the same user, so the latest getter wins.
  batcher.getToken = getToken
  batchersByQuestion.set(key, batcher)
  return batcher
}

/** Whether the caller holds one permission on one record, asked together with every row asking the same this tick. */
export const loadRecordPermission = <Entity extends RecordPermissionsEntity>(
  getToken: () => Promise<string>,
  userId: string,
  { entity, recordId, permission }: AskedRecordPermission<Entity>
): Promise<boolean> => getOrCreateBatcher(getToken, userId, { entity, permission }).loader.load(recordId)
